import {
  Delete,
  EmailLine16,
  EmbeddedLine16,
  InAppLine16,
  PushLine16,
  SmsLine16,
  WebPushLine16,
} from 'ui/icons';
import { getUnique } from 'utils/unique';
import { FlexSpacer } from 'ui/flexSpacer';

import {
  MetaApprovalStatusDefinition,
  MetaQualityRating,
  PseudoApprovalStatusDefinition,
} from 'modules/graphql';
import { MEDIUM_TYPES, MediumSlugMapping, TemplateTableColumn } from './types';
import { WhatsAppMenuItem } from './components/WhatsAppMenuItem';

export enum QUERY_PARAMS {
  SORT_DIRECTION = 'sortDirection',
  SORT_BY = 'sortBy',
  SEARCH = 'search',
  MEDIUM = 'medium',
  CREATORS = 'creators',
  MODIFIERS = 'modifiers',
  LOCALES = 'locales',
  CHANNELS = 'channels',
  PAGE = 'page',
  PAGE_SIZE = 'pageSize',
  PLACEMENTS = 'placements',
  STATUS = 'approvalStatus',
  TYPES = 'templateType',
  QUALITY_RATINGS = 'qualityRatings',
  WHATSAPP_SENDER_STATUS = 'whatsAppSenderStatus',
  WHATSAPP_SENDER_QUALITY_RATING = 'whatsAppSenderQualityRating',
}

export const MODAL_DISPLAY_TEXT_FOR_DELETING_TEMPLATES = {
  actionName: {
    verb: 'delete',
    adjective: 'deleted',
  },
  itemTypeName: {
    singular: 'template',
    plural: 'templates',
  },
  actionIcon: Delete,
};

function addIdAndSortIndex(item: Record<string, unknown>, index: number) {
  return {
    ...item,
    id: getUnique(12),
    sortIndex: index,
  };
}

export const FILTER_PREFIXES = {
  CREATOR: 'creator-',
  MODIFIER: 'modifier-',
  LOCALE: 'locale-',
  CHANNEL_TYPE: 'channel-type-',
  PLACEMENTS: 'placements-',
  STATUS: 'status-',
  WHATSAPP_TYPE: 'whatsapp-type-',
  QUALITY_RATING: 'quality-rating-',
  WHATSAPP_SENDER_STATUS: 'whatsapp-sender-status-',
  WHATSAPP_SENDER_QUALITY_RATING: 'whatsapp-sender-quality-rating-',
};

/**
  These represent back-end GraphQL enums for sorting
 */
export const SORT_BY_FIELD = {
  CREATED_AT: 'CreatedAt',
  ID: 'Id',
  LAST_MODIFIED_BY: 'LastModifiedBy',
  MESSAGE_MEDIUM: 'MessageMedium',
  NAME: 'Name',
  SUBJECT: 'Subject',
  UPDATED_AT: 'UpdatedAt',
};

/**
  The `id` property here maps to the GraphQL Enum for `MessageMediumDefinition`
  However, because we can't rely on the GraphQL enum revealing the proper label we have to 
  explicitly build the object here. Introspection won't work, because we'd need to know how to 
  map the enums to the label, so we might as well hard-code the whole thing
 */
export const MESSAGE_MEDIUM_FILTERS = [
  {
    id: '', // we convert a falsy value to a `null` to avoid using the filter in graphQL, hence the empty id
    label: 'All',
    sortOrder: 0,
  },
  {
    id: 'Email',
    label: 'Email',
    sortOrder: 1,
  },
  {
    id: 'InApp',
    label: 'In-App',
    sortOrder: 2,
  },
  {
    id: 'SMS',
    label: 'SMS',
    sortOrder: 3,
  },
  {
    id: 'Push',
    label: 'Push',
    sortOrder: 4,
  },
  {
    id: 'WebPush',
    label: 'Web Push',
    sortOrder: 5,
  },
  {
    id: 'Embedded',
    label: 'Embedded',
    sortOrder: 6,
  },
  {
    id: 'WhatsApp',
    label: 'WhatsApp',
    sortOrder: 7,
  },
];

export const MEDIUM_TYPE_SLUGS: MediumSlugMapping = {
  [MEDIUM_TYPES.IN_APP]: 'InApp',
  [MEDIUM_TYPES.WEB_PUSH]: 'WebPush',
};

/**
  We link the `ID` value to the sort-by field so we can tie table view clicks on column
  headers to the graphql sorting functionality
 */
export const TEMPLATE_INDEX_COLUMNS: TemplateTableColumn[] = [
  { id: SORT_BY_FIELD.NAME, label: 'Name', isSortable: true },
  { id: SORT_BY_FIELD.MESSAGE_MEDIUM, label: 'Medium', isSortable: true },
  { id: 'subject', label: 'Subject', isSortable: false },
  { id: SORT_BY_FIELD.UPDATED_AT, label: 'Last modified', isSortable: true },
  { id: 'locales', label: 'Locales', isSortable: false },
];

export const TEMPLATE_INDEX_ROW_OPTIONS = [
  {
    label: '',
  },
].map(addIdAndSortIndex);

export enum TEMPLATE_MENU_OPTIONS_KEYS {
  Email = 'email',
  InApp = 'in-app',
  Push = 'push',
  Sms = 'sms',
  WhatsApp = 'whatsApp',
  WebPush = 'web-push',
  Embedded = 'embedded',
}

export type TemplateMenuOptionType = {
  id: string;
  label: string;
  modalLabel: string;
  slug: string;
  content: React.ReactElement;
};

export const TEMPLATE_MENU_OPTIONS: Record<string, TemplateMenuOptionType> = {
  [TEMPLATE_MENU_OPTIONS_KEYS.Email]: {
    id: 'email',
    label: 'Email',
    modalLabel: 'email',
    slug: 'Email',
    content: (
      <FlexSpacer>
        <EmailLine16 /> Email
      </FlexSpacer>
    ),
  },
  [TEMPLATE_MENU_OPTIONS_KEYS.InApp]: {
    id: 'in-app',
    label: 'In-App',
    modalLabel: 'in-app',
    slug: 'InApp',
    content: (
      <FlexSpacer>
        <InAppLine16 /> In-App
      </FlexSpacer>
    ),
  },
  [TEMPLATE_MENU_OPTIONS_KEYS.Push]: {
    id: 'push',
    label: 'Push',
    modalLabel: 'push',
    slug: 'Push',
    content: (
      <FlexSpacer>
        <PushLine16 /> Push
      </FlexSpacer>
    ),
  },
  [TEMPLATE_MENU_OPTIONS_KEYS.Sms]: {
    id: 'sms',
    label: 'SMS',
    modalLabel: 'SMS',
    slug: 'SMS',
    content: (
      <FlexSpacer>
        <SmsLine16 /> SMS
      </FlexSpacer>
    ),
  },
  [TEMPLATE_MENU_OPTIONS_KEYS.WhatsApp]: {
    id: 'whatsApp',
    label: 'WhatsApp',
    modalLabel: 'WhatsApp',
    slug: 'WhatsApp',
    content: <WhatsAppMenuItem />,
  },
  [TEMPLATE_MENU_OPTIONS_KEYS.WebPush]: {
    id: 'web-push',
    label: 'Web Push',
    modalLabel: 'web push',
    slug: 'WebPush',
    content: (
      <FlexSpacer>
        <WebPushLine16 /> Web Push
      </FlexSpacer>
    ),
  },
  [TEMPLATE_MENU_OPTIONS_KEYS.Embedded]: {
    id: 'embedded',
    label: 'Embedded',
    modalLabel: 'embedded',
    slug: 'Embedded',
    content: (
      <FlexSpacer>
        <EmbeddedLine16 /> Embedded
      </FlexSpacer>
    ),
  },
};

export const TEMPLATE_CARD_SORT_OPTIONS = [
  {
    id: 'name',
    label: 'Name',
    value: SORT_BY_FIELD.NAME,
  },
  {
    id: 'medium',
    label: 'Medium',
    value: SORT_BY_FIELD.MESSAGE_MEDIUM,
  },
  {
    id: 'date',
    label: 'Modified Date',
    value: SORT_BY_FIELD.UPDATED_AT,
  },
];

/**
 * Strings used by Segment to differentiate tracked event types that can be triggered
 * from multiple places in the code. For example, this constant would be used to
 * distinguish between deleting a template from the bulk actions bar and deleting
 * a template from a card.
 */
export enum SEGMENT_TRACKED_TEMPLATE_ACTION_TRIGGERS {
  BULK_ACTIONS_BAR = 'Template Index Bulk Actions Bar',
  CARD_ACTIONS = 'Template Index Card Actions',
  TABLE_ROW_ACTIONS = 'Template Index Table Row Actions',
  TEMPLATE_EDITOR_ACTIONS = 'Template Editor Actions',
}

/**
 * The amount of time to wait after a change to the search input textbox before triggering
 * a new search query.
 */
export const SEARCH_TEXT_INPUT_DEBOUNCE_TIMEOUT_MS = 500;

export const FOLDER_ID_SEARCH_PARAM = 'folderId';
export const PAGE_SEARCH_PARAM = 'page';

export const CONTENT_ITEM_NAME = 'template';

export const PREVIEW_NON_HTML_TEMPLATE_URL = '/i/templates/renderhtml?showLighthouseVersion=true&';
export const PREVIEW_HTML_TEMPLATE_URL = '/templates/showHtml?';
export const EDIT_TEMPLATE_URL = '/templates/editor?templateId=';

export type TEMPLATE_INDEX_FILTERS = {
  creators: string[];
  modifiers: string[];
  locales: string[];
  channelTypes: string[];
  placements: string[];
  statuses: string[];
  whatsappTypes: string[];
  qualityRatings: string[];
  whatsAppSenderStatuses: string[];
  whatsAppSenderQualityRatings: string[];
};

export const META_REJECTED_STATUSES = [
  MetaApprovalStatusDefinition.Rejected,
  MetaApprovalStatusDefinition.InAppeal,
  MetaApprovalStatusDefinition.PendingDeletion,
  MetaApprovalStatusDefinition.Deleted,
  MetaApprovalStatusDefinition.Reinstated,
  MetaApprovalStatusDefinition.Flagged,
  MetaApprovalStatusDefinition.FirstPaused,
  MetaApprovalStatusDefinition.SecondPaused,
  MetaApprovalStatusDefinition.Disabled,
  MetaApprovalStatusDefinition.Unknown,
];

export const META_APPROVAL_STATUS_FILTER_VISIBILITY = (isWhatsAppGA: boolean) => ({
  [MetaApprovalStatusDefinition.Approved]: true,
  [MetaApprovalStatusDefinition.Pending]: true,
  [MetaApprovalStatusDefinition.NotSubmitted]: true,
  [MetaApprovalStatusDefinition.Rejected]: true,
  [MetaApprovalStatusDefinition.InAppeal]: false,
  [MetaApprovalStatusDefinition.PendingDeletion]: false,
  [MetaApprovalStatusDefinition.Deleted]: false,
  [MetaApprovalStatusDefinition.Reinstated]: false,
  [MetaApprovalStatusDefinition.Flagged]: false,
  [PseudoApprovalStatusDefinition.Paused]: isWhatsAppGA,
  [MetaApprovalStatusDefinition.FirstPaused]: false,
  [MetaApprovalStatusDefinition.SecondPaused]: false,
  [MetaApprovalStatusDefinition.Disabled]: isWhatsAppGA,
  [MetaApprovalStatusDefinition.Unknown]: false,
});

/** Fixed order in which to sort WhatsApp status template index filters for GA. */
export const WHATSAPP_STATUS_FILTER_ORDER_GA = [
  MetaApprovalStatusDefinition.NotSubmitted,
  MetaApprovalStatusDefinition.Pending,
  MetaApprovalStatusDefinition.Approved,
  MetaApprovalStatusDefinition.Rejected,
  PseudoApprovalStatusDefinition.Paused,
  MetaApprovalStatusDefinition.Disabled,
];

export const WHATSAPP_QUALITY_RATING_FILTER_ORDER = [
  MetaQualityRating.Unknown,
  MetaQualityRating.High,
  MetaQualityRating.Medium,
  MetaQualityRating.Low,
];
